import { DEFAULT_PAGE_INFO } from '@apeiron/common'
import { useFrontendConfigContext } from '@apeiron/config'
import { PlanetMiddleLite } from '@apeiron/planet'
import { PageInfo } from '@apeiron/temp'
import { useLazyQuery } from '@apollo/client'
import { planetMiddleLiteSchema } from '@src/deserialize/yup/planet'
import graphql from '@src/graphql/query/getMySeedPlanetsMiddleLite'
import * as R from 'ramda'
import { useCallback, useState } from 'react'

const useGetMySeedPlanetsMiddleLite = (): Hook => {
  const [fetchPlanets] = useLazyQuery(graphql)

  const { config } = useFrontendConfigContext()

  const [data, setData] = useState<PlanetMiddleLite[]>([])

  const [pageInfo, setPageInfo] = useState<PageInfo>(DEFAULT_PAGE_INFO)

  const fetch = useCallback(
    async (request: Request): Promise<Response> => {
      const { planetIds } = request

      const response = await fetchPlanets({ variables: {} })

      const planets = R.map(
        (planetData: Record<string, any>): PlanetMiddleLite => {
          const planet = planetMiddleLiteSchema.cast(planetData, {
            context: { data: config }
          }) as PlanetMiddleLite

          // ! hard coded to detect seed planet here, should detect by api response later
          const seedPlanet = {
            ...planet,
            seed: true
          }

          return seedPlanet
        },
        R.pathOr([], ['data', 'getMySeedPlanet', 'planets'], response)
      )

      const filteredPlanets = planetIds
        ? R.filter(
            (planet: PlanetMiddleLite) =>
              R.includes(planet.planetID, planetIds),
            planets
          )
        : planets

      const page = {
        ...pageInfo,
        total: R.length(planets),
        totalPage: 1
      }

      setData(filteredPlanets)
      setPageInfo(page)

      return {
        data: filteredPlanets,
        pageInfo: page
      }
    },
    [config, pageInfo, fetchPlanets]
  )

  return {
    data,
    pageInfo,
    fetch
  }
}

type Request = {
  planetIds?: string[]
}

type Response = {
  data: PlanetMiddleLite[]
  pageInfo: PageInfo
}

type Hook = {
  data: PlanetMiddleLite[]
  pageInfo: PageInfo
  fetch: (request: Request) => Promise<Response>
}

export default useGetMySeedPlanetsMiddleLite
